// ** react imports
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

// ** react router imports
import { useParams } from 'react-router-dom';

// ** api hooks
import { useGetTaxFileHistory } from '../../../../hooks/apis/useTaxFile';

// ** components imports
import TimelineStepper from './TimelineStepper';
import Loading from '../../../common/Loading';

const TaxFileHistory = props => {
  // hooks
  const { taxFileId } = useParams();

  // apis hooks
  const {
    data: historyData,
    isGetTaxFileHistoryLoading,
    getTaxFileHistory,
  } = useGetTaxFileHistory();

  // hooks
  useMemo(() => {
    if (taxFileId) {
      getTaxFileHistory(taxFileId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxFileId]);

  // handlers
  const formatDate = dateString => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getEventIcon = event => {
    if (event === 'tax_file.created') {
      return <i className="ri-file-add-line" aria-hidden="true"></i>;
    }
    if (event === 'tax_file.document_uploaded') {
      return <i className="ri-upload-line" aria-hidden="true"></i>;
    }
    if (event === 'tax_file.document_removed') {
      return <i className="ri-delete-bin-line" aria-hidden="true"></i>;
    }
    if (event === 'tax_file.document_replaced') {
      return <i className="ri-file-transfer-line" aria-hidden="true"></i>;
    }
    if (event === 'tax_file.document_downloaded') {
      return <i className="ri-download-line" aria-hidden="true"></i>;
    }
    if (event === 'tax_file.viewed_by_admin') {
      return <i className="ri-eye-line" aria-hidden="true"></i>;
    }
    if (event === 'tax_file.status_updated') {
      return <i className="ri-refresh-line" aria-hidden="true"></i>;
    }
    if (event === 'tax_file.notes_added') {
      return <i className="ri-sticky-note-line" aria-hidden="true"></i>;
    }
    if (event === 'tax_file.assigned_to_manager') {
      return <i className="ri-user-shared-line" aria-hidden="true"></i>;
    }
    if (event === 'tax_file.content_copied') {
      return <i className="ri-file-copy-line" aria-hidden="true"></i>;
    }

    if (event === 'quiz_response.created') {
      return <i className="ri-file-add-line" aria-hidden="true"></i>;
    }
    if (event === 'quiz_response.section_answer_submitted') {
      return <i className="ri-send-plane-line" aria-hidden="true"></i>;
    }
    if (event === 'quiz_response.section_answer_updated_by_admin') {
      return <i className="ri-edit-line" aria-hidden="true"></i>;
    }
    if (event === 'quiz_response.section_answer_saved_as_draft') {
      return <i className="ri-draft-line" aria-hidden="true"></i>;
    }
    if (event === 'quiz_response.section_answer_saved_as_draft_by_admin') {
      return <i className="ri-file-edit-line" aria-hidden="true"></i>;
    }
    if (event === 'quiz_response.section_document_uploaded') {
      return <i className="ri-upload-line" aria-hidden="true"></i>;
    }
    if (event === 'quiz_response.section_document_deleted') {
      return <i className="ri-delete-bin-line" aria-hidden="true"></i>;
    }
    if (event === 'quiz_response.section_document_replaced') {
      return <i className="ri-file-transfer-line" aria-hidden="true"></i>;
    }
    if (event === 'quiz_response.document_downloaded') {
      return <i className="ri-download-line" aria-hidden="true"></i>;
    }
    if (event === 'quiz_response.file_viewed_by_admin') {
      return <i className="ri-eye-line" aria-hidden="true"></i>;
    }
    if (event === 'quiz_response.content_copied_by_admin') {
      return <i className="ri-file-copy-line" aria-hidden="true"></i>;
    }
    if (event === 'quiz_response.answer_updated') {
      return <i className="ri-pencil-line" aria-hidden="true"></i>;
    }
    return <i className="ri-information-line" aria-hidden="true"></i>;
  };

  const getActorName = actor => {
    if (!actor) return 'System';
    return actor.fullName || actor.email || 'Unknown User';
  };

  return (
    <div className="w-full h-[calc(70vh-100px)] max-h-[calc(70vh-100px)]  relative overflow-auto pl-2">
      {isGetTaxFileHistoryLoading && historyData.length === 0 ? (
        <div className="min-h-[calc(70vh-100px)] bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm z-40 flex items-center justify-center rounded-xl">
          <div className="flex items-center justify-center gap-3 text-gray-600 dark:text-gray-300">
            <Loading size="medium" />
          </div>
        </div>
      ) : (
        <>
          {historyData && historyData.length > 0 && (
            <div className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 text-blue-700 dark:text-blue-400 text-xs font-medium rounded-full">
              <div className="w-1.5 h-1.5 bg-blue-500 dark:bg-blue-400 rounded-full animate-pulse"></div>
              <span>
                {historyData.length}{' '}
                {historyData.length === 1 ? 'activity' : 'activities'} recorded
              </span>
            </div>
          )}

          <TimelineStepper
            items={historyData}
            formatDate={formatDate}
            getEventIcon={getEventIcon}
            getActorName={getActorName}
          />
        </>
      )}
    </div>
  );
};

TaxFileHistory.propTypes = {
  taxFileId: PropTypes.string,
};

export default TaxFileHistory;
